import * as A from 'astronomy-engine';
import { useEffect, useRef, useState } from 'preact/hooks';
import { services } from '../app';
import { skyBrightnessAt } from '../astro/lightpollution';
import { calendarFilmOpen, darkOut, introOpen, loadJSON, onceOfferShown, panel, place, pointingPaused, previewFrom, reliving, selection, setSetting, settings, timeLive, viewMode, whatToShow, storyOpen, saveJSON, showToast } from '../state';
import { alertState, enableAlerts, refreshAlertState, syncAlerts, updateAlertPlace } from '../live/push';
import { watchTonight } from '../live/skycheck';
import { watchSaudi } from '../live/saudi';
import { cameraHelpPaused, watchCameraHelp } from '../live/camerahelp';
import { satStatus } from '../live/satellites';
import { loadManifest } from '../audio/narrator';
import { APP_NAME, APP_NAME_AR } from '../data/brand';
import { BandBanner, CoachMark, Dock, GuideChip, NightFilter, AlignChip, PausedChip, PointingChip, PointPrompt, PreviewBanner, SurveyCredit, Toast, TopBar } from './chrome';
import { autoStartPointing, usePointingWatch } from './pointing';
import { CameraChip, CameraNote } from './CameraHelp';
import { CAMERA_HELP_OFFERED } from '../data/camerahelp';
import { WhatToShowSheet } from './WhatToShow';
import { CalendarFilm, IntroFilm } from './IntroFilm';
import { DarkCheck, DarkCheckOffer, darkCheckOpen, darkOfferShown } from './DarkCheck';
import { DayHint } from './DayHint';
import { DarkSitesPanel } from './DarkSitesPanel';
import { HuntPlayer } from './HuntPlayer';
import { HelpPanel } from './HelpPanel';
import { GuideOffer, Walkthrough, guideOffer } from './Guide';
import { AlignCard, AlignOffer } from './Align';
import { aligning } from '../live/align';
import { OnceOffers } from './OnceOffer';
import { IslamPanel } from './IslamPanel';
import { NamePanel } from './NamePanel';
import { CalendarPanel } from './CalendarPanel';
import { useCrescentFrame } from './Crescent';
import { OwnerPanel } from './OwnerPanel';
import { GalleryPanel } from './GalleryPanel';
import { OnNowChip } from './OnNow';
import { InfoPanel } from './InfoPanel';
import { LayersPanel } from './LayersPanel';
import { LearnPanel, LessonPlayer, QuizPlayer } from './LearnPanel';
import { NightPage, NightsPanel, ReliveBanner, ReliveBar } from './NightsPanel';
import { WhatsNewCard, WhatsNewPanel, filmPending, markNewsSeen, newsPending, storyPending } from './WhatsNew';
import { UpdateStory } from './UpdateStory';
import { Onboarding } from './Onboarding';
import { SearchPanel } from './SearchPanel';
import { LocationPanel, SettingsPanel } from './SettingsPanel';
import { SkyView } from './SkyView';
import { SizeCompare } from './SizeCompare';
import { TimePanel } from './TimePanel';
import { TonightPanel } from './TonightPanel';
import { TourPlayer } from './TourPlayer';
import { MorePanel } from './MorePanel';
import { FeedbackPanel } from './FeedbackPanel';
import { watchTrail } from './trail';
import { watchOutbox } from '../live/feedback';
import { StartVerse } from './StartVerse';

function Panels() {
  switch (panel.value) {
    case 'search': return <SearchPanel />;
    case 'tonight': return <TonightPanel />;
    case 'layers': return <LayersPanel />;
    case 'settings': return <SettingsPanel />;
    case 'location': return <LocationPanel />;
    case 'time': return <TimePanel />;
    case 'tour': return <TourPlayer />;
    case 'go': return <DarkSitesPanel />;
    case 'hunt': return <HuntPlayer />;
    case 'help': return <HelpPanel />;
    case 'walkthrough': return <Walkthrough />;
    case 'learn': return <LearnPanel />;
    case 'lesson': return <LessonPlayer />;
    case 'quiz': return <QuizPlayer />;
    case 'nights': return <NightsPanel />;
    case 'night': return <NightPage />;
    case 'whatsnew': return <WhatsNewPanel />;
    case 'islam': return <IslamPanel />;
    case 'name': return <NamePanel />;
    case 'calendar': return <CalendarPanel />;
    case 'gallery': return <GalleryPanel />;
    case 'sizes': return <SizeCompare />;
    case 'owner': return <OwnerPanel />;
    case 'more': return <MorePanel />;
    case 'feedback': return <FeedbackPanel />;
    default: return null;
  }
}

/** Keep the screen awake while pointing at the sky (or paused, with the phone still in hand). */
function useWakeLock() {
  useEffect(() => {
    let lock: { release: () => Promise<void> } | null = null;
    const want = () => (viewMode.peek() === 'ar' || !!pointingPaused.peek()) && document.visibilityState === 'visible';
    const sync = async () => {
      const nav = navigator as unknown as { wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> } };
      try {
        if (want() && !lock && nav.wakeLock) lock = await nav.wakeLock.request('screen');
        if (!want() && lock) {
          await lock.release();
          lock = null;
        }
      } catch {
        lock = null;
      }
    };
    const unsub = viewMode.subscribe(() => void sync());
    const unsubPaused = pointingPaused.subscribe(() => void sync());
    document.addEventListener('visibilitychange', sync);
    return () => {
      unsub();
      unsubPaused();
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);
}

/** The Sun's altitude (degrees) at a place and moment. */
function sunAltitude(lat: number, lon: number, t: Date): number {
  const obs = new A.Observer(lat, lon, 0);
  const e = A.Equator(A.Body.Sun, t, obs, true, true);
  return A.Horizon(t, obs, e.ra, e.dec, 'normal').altitude;
}

/**
 * Whether it is dark where the user really is, so the interface can dim itself; and whether the sky
 * on screen is a daytime one (in the past or future) that would dazzle eyes used to the dark.
 */
function useDarkOutside(): boolean {
  const [dayScrub, setDayScrub] = useState(false);
  useEffect(() => {
    const check = () => {
      // Where the user really is: their home place while previewing somewhere else.
      const p = previewFrom.peek()?.home ?? place.peek();
      darkOut.value = sunAltitude(p.lat, p.lon, new Date()) < -6;
      const s = services.peek();
      setDayScrub(darkOut.peek() && !timeLive.peek() && !!s && s.engine.context.sunAlt > -4);
    };
    check();
    const id = setInterval(check, 2000);
    const off = place.subscribe(check);
    return () => {
      clearInterval(id);
      off();
    };
  }, []);
  return dayScrub;
}

/** Estimate how light-polluted the sky is wherever the user is (or is previewing). */
function useAutoSkyBrightness() {
  useEffect(() => {
    let seq = 0;
    const run = async () => {
      if (!settings.peek().bortleAuto) return;
      const p = place.peek();
      const k = ++seq;
      const b = await skyBrightnessAt(p.lat, p.lon);
      if (!b || k !== seq || !settings.peek().bortleAuto) return;
      const sqm = Math.round(b.sqm * 100) / 100;
      if (settings.peek().sqm !== sqm) setSetting('sqm', sqm);
      if (settings.peek().bortle !== b.bortle) setSetting('bortle', b.bortle);
    };
    const u1 = place.subscribe(() => void run());
    let auto = settings.peek().bortleAuto;
    const u2 = settings.subscribe((s) => {
      if (s.bortleAuto && !auto) void run();
      auto = s.bortleAuto;
    });
    return () => {
      u1();
      u2();
    };
  }, []);
}

/**
 * Keep alerts current: the home place when it changes, and the next few days of ISS passes on each
 * visit (once the orbit data has loaded, and again when the app comes back to the foreground).
 */
function useAlertSync() {
  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | null = null;
    const offPlace = place.subscribe(() => {
      if (previewFrom.peek()) return;
      if (t) clearTimeout(t);
      t = setTimeout(() => void updateAlertPlace(), 2000);
    });
    let synced = false;
    let syncedRev = -1;
    let later: ReturnType<typeof setTimeout> | undefined;
    const offSats = satStatus.subscribe((st) => {
      // The first catalogue, then each newer one: new orbits move the space station's pass times, so that sync goes
      // past the usual 20-minute wait. A catalogue counts as sent only once a sync with it has gone through.
      if (!st.count || st.rev === syncedRev) return;
      const first = !synced, rev = st.rev;
      synced = true;
      clearTimeout(later);
      later = setTimeout(() => void syncAlerts(!first).then((ok) => ok && (syncedRev = rev)), 3000);
    });
    const onVis = () => document.visibilityState === 'visible' && synced && void syncAlerts();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      offPlace();
      offSats();
      clearTimeout(later);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);
}

export function App() {
  const [error, setError] = useState<string | null>(null);
  const [onboarded, setOnboarded] = useState(() => !!loadJSON<{ v?: number }>('onboarded', {}).v);
  // Pointing first on a phone. A first visit chooses in the welcome instead.
  const [onboardedAtOpen] = useState(onboarded);
  // After an update with something new to see, a short note comes first (a new install has the welcome). When
  // the update brought a film, the film is offered instead; the note follows only if it is declined, or for
  // older news not yet seen.
  const [film, setFilm] = useState(() => (onboardedAtOpen ? filmPending() : null));
  // An update told in pages comes in place of the card.
  const [story, setStory] = useState(() => (onboardedAtOpen ? storyPending() : null));
  const [skipNews, setSkipNews] = useState<string | undefined>(undefined);
  const [news, setNews] = useState(() => onboardedAtOpen && (!!film || !!story || newsPending()));
  const filmDone = (watched: boolean) => {
    const skip = watched ? film?.id : undefined;
    setFilm(null);
    if (newsPending(skip)) setSkipNews(skip);
    else {
      markNewsSeen();
      setNews(false);
    }
  };
  const newsShown = useRef(news);
  useEffect(() => {
    if (!onboardedAtOpen) markNewsSeen();
  }, []);
  // Feedback: the last few things looked at, and any message waiting to be sent (src/ui/FeedbackPanel.tsx).
  useEffect(() => {
    const stopTrail = watchTrail();
    const stopOutbox = watchOutbox();
    return () => {
      stopTrail();
      stopOutbox();
    };
  }, []);
  useWakeLock();
  usePointingWatch();
  useAutoSkyBrightness();
  useAlertSync();
  // Tonight's "I can't see it" ends at sunrise.
  useEffect(() => watchTonight(), []);
  // Saudi Arabia's decided month starts, kept fresh (often while the date at home waits for an announcement).
  useEffect(() => watchSaudi(() => previewFrom.peek()?.home ?? place.peek()), []);
  useCrescentFrame();
  // Camera help looks while it is on and pointing (src/live/camerahelp.ts), while it is offered at all.
  useEffect(() => (CAMERA_HELP_OFFERED ? watchCameraHelp() : undefined), []);
  // The list of recorded clips, so name buttons only appear where a recording exists; and whether
  // evening alerts are on.
  useEffect(() => {
    void loadManifest();
    void refreshAlertState();
  }, []);
  // Moved in from the old address with notifications on there: they have to be allowed again here, once the update's
  // own news has been seen. One tap turns them back on (an iPhone asks only after a tap).
  useEffect(() => {
    if (news) return;
    const moved = loadJSON<{ notifications?: boolean; told?: boolean }>('moved', {});
    if (!moved.notifications || moved.told) return;
    void refreshAlertState().then(() => {
      if (alertState.peek() !== 'off') return;
      saveJSON('moved', { ...moved, told: true });
      showToast('Notifications are off at the new address. Turn them on again for your reminders.', 15000, { label: 'Turn on', run: () => void enableAlerts() });
    });
  }, [news]);
  const dayScrub = useDarkOutside();
  const ready = !!services.value;
  useEffect(() => {
    if (!ready || !onboardedAtOpen || news) return;
    // Straight after the note is closed, the tap that closed it would let an iPhone ask for motion access
    // there and then (over whatever it opened): wait until that tap no longer counts, so it asks as usual.
    if (!newsShown.current) return void autoStartPointing();
    const id = setTimeout(() => void autoStartPointing(), 1500);
    return () => clearTimeout(id);
  }, [ready, news]);
  const p = panel.value;
  // The darkness check has the sky to itself: no card, dock or other offers while it runs, and camera help rests (the
  // check asks the person's own eyes).
  const checking = darkCheckOpen.value;
  useEffect(() => void (cameraHelpPaused.value = checking), [checking]);
  const showInfo = !!selection.value && (p === null) && !checking;
  return (
    <div class={`app ${settings.value.nightMode ? 'night' : ''} ${darkOut.value ? 'dark-out' : ''} ${viewMode.value === 'ar' ? 'is-ar' : ''} ${aligning.value ? 'is-aligning' : ''} ${reliving.value ? 'is-reliving' : ''}`}>
      <SkyView onError={(e) => setError(e.message)} />
      {dayScrub && <div class="sky-dim" aria-hidden="true" />}
      {/* Until the welcome is done, nothing but the sky behind it. */}
      {ready && onboarded && (
        <>
          <TopBar />
          <PreviewBanner />
          {!news && !whatToShow.value && !introOpen.value && !calendarFilmOpen.value && !storyOpen.value && !checking && <OnNowChip />}
          <ReliveBanner />
          <BandBanner />
          <SurveyCredit />
          <PointingChip />
          <PausedChip />
          <PointPrompt />
          <AlignChip />
          <AlignCard />
          {CAMERA_HELP_OFFERED && <CameraChip />}
          {CAMERA_HELP_OFFERED && <CameraNote />}
          <GuideChip />
          <CoachMark />
          {!news && !whatToShow.value && !introOpen.value && !calendarFilmOpen.value && !storyOpen.value && <DayHint />}
          {/* "Show me around" shows the dock even while a night is relived: it is what the tour points at. */}
          {(p === 'walkthrough' || (!showInfo && !checking && !reliving.value && !aligning.value && p !== 'tour' && p !== 'hunt' && p !== 'lesson' && p !== 'quiz')) && <Dock />}
          {reliving.value && !showInfo && !p && !checking && <ReliveBar />}
          <Panels />
          {showInfo && <InfoPanel />}
          {whatToShow.value && !p && !showInfo && <WhatToShowSheet />}
          {news && !film && story && !whatToShow.value && !p && !showInfo && !reliving.value && !checking && (
            <UpdateStory id={story} onDone={() => {
              // Older notes not yet seen (an update missed before this one) follow on the card.
              if (newsPending(story)) setSkipNews(story);
              else {
                markNewsSeen();
                setNews(false);
              }
              setStory(null);
            }} />
          )}
          {news && !film && !story && !whatToShow.value && !p && !showInfo && !reliving.value && !checking && (
            <WhatsNewCard skip={skipNews} onDone={() => { markNewsSeen(); setNews(false); }} />
          )}
          {news && film && !whatToShow.value && !reliving.value && <CalendarFilm onDone={() => filmDone(true)} onLater={() => filmDone(false)} />}
          {/* A new user is offered "Show me around" once, at a quiet moment after the welcome. */}
          <GuideOffer blocked={news || checking || darkOfferShown.value} />
          {/* Offered once at a quiet moment: Arabic names to an install that has them off, "The stars in Islam" after the tour. */}
          <OnceOffers blocked={news || checking || darkOfferShown.value} />
          {/* Align with the sky: offered once a night after dark, in pointing mode, when nothing else is being offered. */}
          <AlignOffer blocked={news || checking || darkOfferShown.value || onceOfferShown.value || guideOffer.value === 'shown'} />
          {/* The darkness check: offered once per place after dark (Naked eye), and the check itself. */}
          <DarkCheckOffer blocked={news} />
          {checking && <DarkCheck />}
          {introOpen.value && <IntroFilm onDone={() => (introOpen.value = false)} />}
          {calendarFilmOpen.value && <CalendarFilm onDone={() => (calendarFilmOpen.value = false)} />}
          {storyOpen.value && <UpdateStory id={storyOpen.value} onDone={() => (storyOpen.value = null)} />}
        </>
      )}
      {ready && <Toast />}
      {!ready && !error && (
        <div class="boot">
          <div class="boot-inner">
            <div class="brand">
              <span>{APP_NAME}</span>
              <span class="arabic-script" lang="ar" dir="rtl">{APP_NAME_AR}</span>
            </div>
            <StartVerse />
          </div>
        </div>
      )}
      {error && <div class="fatal">{error}</div>}
      {ready && !onboarded && <Onboarding onDone={() => setOnboarded(true)} />}
      <NightFilter />
    </div>
  );
}
